<template>
  <section class="pro">
    <!-- ── Partie haute : pitch écoles ── -->
    <div class="pro__pitch">
      <div class="container pro__pitch-inner">
        <div class="pro__content">
          <h2 class="pro__title">
            {{ $t('pro.titleWhite') }}<br />
            <span class="pro__title-orange">{{ $t('pro.titleOrange') }}</span>
          </h2>
          <p class="pro__desc">{{ $t('pro.desc') }}</p>
          <NuxtLink
            to="https://edu.prepeers.co/"
            external
            target="_blank"
            rel="noopener noreferrer"
            class="btn pro__cta">
            {{ $t('pro.cta') }}
          </NuxtLink>
        </div>

        <div class="pro__visual">
          <img
            :src="PRO_ILLUSTRATION_SRC"
            alt="PrePeers Pro"
            class="pro__illustration"
            width="380"
            height="280"
            loading="lazy"
            decoding="async"
            onerror="this.style.display = 'none'" />
        </div>
      </div>
    </div>

    <!-- ── Partie basse : logos partenaires ── -->
    <div class="pro__partners">
      <div class="container">
        <h3 class="pro__partners-title">{{ $t('pro.partnersTitle') }}</h3>

        <!-- Carrousel logos -->
        <div
          ref="viewportRef"
          class="pro__logos-viewport">
          <div
            class="pro__logos-track"
            :style="{ transform: `translateX(${offset}px)` }">
            <div
              v-for="partner in proPartners"
              :key="partner.schoolname"
              class="pro__logo-slide">
              <div class="pro__logo-wrap">
                <template v-if="partner.schoollogourl">
                  <img
                    :src="partner.schoollogourl"
                    :alt="partner.schoolname"
                    class="pro__logo-img"
                    width="110"
                    height="110"
                    loading="lazy"
                    decoding="async"
                    onerror="
                      this.style.display = 'none'
                      this.nextElementSibling.style.display = 'flex'
                    " />
                  <span class="pro__logo-fallback">{{ partner.schoolname }}</span>
                </template>
                <span
                  v-else
                  class="pro__logo-fallback pro__logo-fallback--visible"
                  >{{ partner.schoolname }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Dots -->
        <div class="pro__dots">
          <button
            v-for="(_, i) in dotCount"
            :key="i"
            class="pro__dot"
            :class="{ 'pro__dot--active': activeDot === i }"
            :aria-label="`Partenaire ${i + 1}`"
            @click="goTo(i)" />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { ComputedRef, Ref } from 'vue'

const { proPartners } = useProPartnersData()

const SLIDES_DESKTOP: number = 5
const SLIDES_MOBILE: number = 2
const GAP: number = 24
const PRO_ILLUSTRATION_SRC: string = '/images/pro/pro-illustration.svg'

const viewportRef: Ref<HTMLDivElement | null> = ref<HTMLDivElement | null>(null)
const activeDot: Ref<number> = ref<number>(0)
const offset: Ref<number> = ref<number>(0)
const slidesVisible: Ref<number> = ref<number>(SLIDES_DESKTOP)

let touchStartX: number = 0

const dotCount: ComputedRef<number> = computed((): number =>
  Math.ceil(proPartners.value.length / slidesVisible.value),
)

/**
 * Calcule la largeur d'une slide du carrousel logos.
 * @returns {number} Largeur en pixels, ou 0 si le viewport n'est pas monté.
 */
function slideWidth(): number {
  if (!viewportRef.value) return 0
  return (viewportRef.value.offsetWidth - (slidesVisible.value - 1) * GAP) / slidesVisible.value
}

/**
 * Navigue vers la page de dots indiquée.
 * @param {number} index - Index de la page cible.
 */
function goTo(index: number): void {
  activeDot.value = Math.min(index, dotCount.value - 1)
  const sw: number = slideWidth()
  offset.value = -(activeDot.value * slidesVisible.value * (sw + GAP))
}

/**
 * Adapte le nombre de slides visibles selon la largeur du viewport.
 */
function checkBreakpoint(): void {
  slidesVisible.value = window.innerWidth < 640 ? SLIDES_MOBILE : SLIDES_DESKTOP
  goTo(0)
}

/**
 * Enregistre la position de départ du swipe tactile.
 * @param {TouchEvent} e - Événement touchstart.
 */
function onTouchStart(e: TouchEvent): void {
  touchStartX = e.touches[0]?.clientX ?? 0
}

/**
 * Applique le swipe tactile pour changer de page de logos.
 * @param {TouchEvent} e - Événement touchend.
 */
function onTouchEnd(e: TouchEvent): void {
  const diff: number = touchStartX - (e.changedTouches[0]?.clientX ?? 0)
  if (diff > 40 && activeDot.value < dotCount.value - 1) goTo(activeDot.value + 1)
  else if (diff < -40 && activeDot.value > 0) goTo(activeDot.value - 1)
}

onMounted((): void => {
  checkBreakpoint()
  window.addEventListener('resize', checkBreakpoint)
  viewportRef.value?.addEventListener('touchstart', onTouchStart, { passive: true })
  viewportRef.value?.addEventListener('touchend', onTouchEnd, { passive: true })
})

onUnmounted((): void => {
  window.removeEventListener('resize', checkBreakpoint)
  viewportRef.value?.removeEventListener('touchstart', onTouchStart)
  viewportRef.value?.removeEventListener('touchend', onTouchEnd)
})
</script>

<style scoped>
/* ── Section globale ── */
.pro {
  overflow: hidden;
}

/* ── Partie haute ── */
.pro__pitch {
  background: linear-gradient(105deg, #3f1c16 0%, #241f36 100%);
  padding-block: var(--space-16);
}

.pro__pitch-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-12);
  align-items: center;
}

.pro__content {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.pro__title {
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 800;
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-tight);
  color: var(--text-primary);
}

.pro__title-orange {
  color: var(--accent-orange);
}

.pro__desc {
  font-size: var(--text-base);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
  max-width: 480px;
}

.pro__cta {
  width: fit-content;
  background: var(--accent-orange);
  color: #fff;
  font-weight: 700;
  font-size: var(--text-base);
  padding: 0.75em 2em;
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: filter var(--duration-fast) var(--ease-out);
  box-shadow: var(--shadow-orange);
  margin-top: var(--space-2);
}

.pro__cta:hover {
  filter: brightness(1.1);
}

/* Illustration 3D */
.pro__visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pro__illustration {
  max-width: 380px;
  width: 100%;
  object-fit: contain;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.4));
}

/* ── Partie basse : partenaires ── */
.pro__partners {
  background: #1a1525;
  padding-block: var(--space-12);
}

.pro__partners-title {
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: 700;
  color: var(--text-primary);
  text-align: center;
  margin-bottom: var(--space-10);
}

/* Carrousel logos */
.pro__logos-viewport {
  overflow: hidden;
  width: 100%;
}

.pro__logos-track {
  display: flex;
  gap: var(--space-6);
  transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform;
}

.pro__logo-slide {
  flex-shrink: 0;
  width: calc((100% - 4 * var(--space-6)) / 5);
  display: flex;
  justify-content: center;
}

.pro__logo-wrap {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  border: 2px solid var(--border-subtle);
  background: var(--bg-elevated);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color var(--duration-base) var(--ease-out);
}

.pro__logo-wrap:hover {
  border-color: var(--border-default);
}

.pro__logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: var(--space-1);
  border-radius: 100%;
}

.pro__logo-fallback {
  display: none;
  font-size: var(--text-xs);
  font-weight: 700;
  color: var(--text-muted);
  text-align: center;
  padding: var(--space-2);
}

.pro__logo-fallback--visible {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

/* Dots */
.pro__dots {
  display: flex;
  justify-content: center;
  gap: var(--space-2);
  margin-top: var(--space-8);
}

.pro__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  cursor: pointer;
  padding: 0;
  transition:
    background var(--duration-base) var(--ease-out),
    width var(--duration-base) var(--ease-out);
}

.pro__dot--active {
  background: var(--accent-orange);
  width: 24px;
  border-radius: var(--radius-full);
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .pro__pitch-inner {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .pro__visual {
    order: -1;
  }

  .pro__illustration {
    max-width: 260px;
  }

  .pro__desc {
    margin-inline: auto;
  }

  .pro__cta {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 640px) {
  .pro__logo-slide {
    width: calc((100% - var(--space-6)) / 2);
  }

  .pro__logo-wrap {
    width: 90px;
    height: 90px;
  }
}
</style>
